import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
	AuthCaption,
	AuthHeading,
	AuthLayout,
	AuthLeftBlock,
	AuthRightBlock,
	Button,
	RadioCard,
	Stepper,
} from '@rentflowdev/ui'
import { useEffect, useState } from 'react'
import { useUpdateAccountTypeMutation, useUpdateTaxRegimeMutation } from '@/api'
import type { AccountType as ApiAccountType } from '@/types'
import { useFormDraft } from '@/lib/wizard-drafts-store'

export type AccountType = 'individual' | 'entrepreneur' | 'company'

const TO_API: Record<AccountType, ApiAccountType> = {
	individual:   'INDIVIDUAL',
	entrepreneur: 'ENTREPRENEUR',
	company:      'COMPANY',
}

export interface OnboardingDraft {
	accountType: AccountType | null
}

export const ONBOARDING_DRAFT_KEY = 'onboarding'

export const EMPTY_ONBOARDING_DRAFT: OnboardingDraft = {
	accountType: null,
}

/** An individual has no tax regime to choose, so their flow skips that step. */
export const onboardingTotalSteps = (accountType: AccountType | null | undefined) =>
	accountType === 'individual' ? 2 : 3

const ACCOUNT_TYPES: AccountType[] = ['individual', 'entrepreneur', 'company']

export const Route = createFileRoute('/onboarding/account-type')({
	component: AccountTypePage,
})

function AccountTypePage() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const draft = useFormDraft<OnboardingDraft>(ONBOARDING_DRAFT_KEY)
	const initial = draft.read()?.state
	const [updateAccountType, { isLoading: savingType }] = useUpdateAccountTypeMutation()
	const [updateTaxRegime, { isLoading: savingRegime }] = useUpdateTaxRegimeMutation()
	const [error, setError] = useState<string | null>(null)

	const [selected, setSelected] = useState<AccountType>(
		initial?.accountType ?? 'individual',
	)

	useEffect(() => {
		draft.write({ ...EMPTY_ONBOARDING_DRAFT, ...draft.read()?.state, accountType: selected })
	}, [selected, draft])

	const handleNext = async () => {
		setError(null)
		try {
			await updateAccountType({ accountType: TO_API[selected] }).unwrap()
			if (selected === 'individual') {
				// Settings show this regime preselected for an individual account.
				await updateTaxRegime({ taxRegime: 'INDIVIDUAL' }).unwrap()
				void navigate({ to: '/onboarding/profile' })
			} else {
				void navigate({ to: '/onboarding/tax-regime' })
			}
		} catch {
			setError(t('onboarding.accountType.saveError'))
		}
	}

	const saving = savingType || savingRegime

	return (
		<AuthLayout>
			<AuthLeftBlock />

			<AuthRightBlock>
				<div className="flex flex-col w-full">
					<Stepper current={1} total={onboardingTotalSteps(selected)} className="min-h-15" />

					<div className="flex flex-col gap-1">
						<AuthHeading className="text-left">{t('onboarding.accountType.title')}</AuthHeading>
						<AuthCaption className="text-left text-base leading-6">
							{t('onboarding.accountType.subtitle')}
						</AuthCaption>
					</div>

					<div className="flex flex-col gap-3 py-6">
						{ACCOUNT_TYPES.map((type) => (
							<RadioCard
								key={type}
								title={t(`onboarding.accountType.${type}.title`)}
								selected={selected === type}
								onClick={() => setSelected(type)}
								onKeyDown={(e) => e.key === 'Enter' && setSelected(type)}
							/>
						))}
						{error && <p className="text-sm text-(--color-destructive)">{error}</p>}
					</div>

					<Button size="full" onClick={() => void handleNext()} loading={saving}>
						{t('common.next')}
					</Button>
				</div>
			</AuthRightBlock>
		</AuthLayout>
	)
}
